import { buildSocketExpressionsFromEdges } from "./expressions";
import { isFlowSocket } from "./types";

interface DerivableSocket {
  id: string;
  expression?: string | undefined;
  manualOverride?: boolean | undefined;
}

interface DerivableStep<S extends DerivableSocket> {
  id: string;
  inputSockets: S[];
}

interface DerivableEdge {
  sourceStepId: string;
  sourceSocketId: string;
  targetStepId: string;
  targetSocketId: string;
}

/** A persisted manual override: admin-authored, no meaningful backing edge. */
function isManualOverride(socket: DerivableSocket): boolean {
  return socket.manualOverride === true && Boolean(socket.expression);
}

/**
 * Recompute each step's input-socket expressions from the edge set, mirroring
 * what the editor save path persists (`mapStepInputToModel`, minus the
 * client-input handling):
 *
 * - a data-wired socket gets the edge-derived expression;
 * - a non-override socket with no backing data edge has its expression cleared;
 * - a persisted manual override (`manualOverride` + `expression`) is left
 *   untouched, edge or no edge.
 *
 * Pure. Edge order determines the order of a fan-in `(collect ...)`, so pass
 * edges in a deterministic order for idempotent results.
 */
export function deriveSocketExpressions<S extends DerivableSocket>(
  steps: ReadonlyArray<DerivableStep<S>>,
  edges: readonly DerivableEdge[],
): Array<{ stepId: string; inputSockets: S[]; changed: boolean }> {
  const derived = buildSocketExpressionsFromEdges([...edges]);
  return steps.map((step) => {
    let changed = false;
    const inputSockets = step.inputSockets.map((socket) => {
      if (isManualOverride(socket)) return socket;
      const desired = derived.get(`${step.id}:${socket.id}`);
      if (socket.expression === desired && !socket.manualOverride) {
        return socket;
      }
      changed = true;
      // Drop any dangling manualOverride flag (no expression to protect),
      // as the save path does.
      const { manualOverride: _flag, ...rest } = socket;
      return { ...rest, expression: desired } as S;
    });
    return { stepId: step.id, inputSockets, changed };
  });
}

/**
 * Detect half-converted steps: a step with at least one socket expression
 * (derived or manual override) that also has a data-wired (non-flow) input
 * socket without one. With the expression resolver enabled, those wired
 * sockets would resolve to nothing.
 *
 * Scoped per step, like the runtime: `resolveStepInputsWithDualRead` compares
 * and switches encodings step by step, and a step with no expression at all
 * keeps its edge inputs under either flag. Overrides count as expressions here
 * because a step with only overrides plus unconverted wired sockets is exactly
 * the case that loses inputs.
 *
 * Returns the offending `${stepId}:${socketId}` keys (empty when every step
 * is fully converted or has no expressions at all).
 */
export function findWiredSocketsMissingExpressions(
  steps: ReadonlyArray<DerivableStep<DerivableSocket>>,
  edges: readonly DerivableEdge[],
): string[] {
  const wired = new Set<string>();
  for (const edge of edges) {
    if (isFlowSocket(edge.targetSocketId)) continue;
    wired.add(`${edge.targetStepId}:${edge.targetSocketId}`);
  }

  const missing: string[] = [];
  for (const step of steps) {
    if (!step.inputSockets.some((socket) => Boolean(socket.expression))) {
      continue;
    }
    for (const socket of step.inputSockets) {
      const key = `${step.id}:${socket.id}`;
      if (wired.has(key) && !socket.expression) missing.push(key);
    }
  }
  return missing;
}
